// src/pages/Empresas.jsx
import React, { useState, useEffect } from 'react';
import {
  getFirestore,
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
  Timestamp,
  query,
  limit,
  startAfter,
  orderBy,
  where
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from '../firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import { FaPlus, FaArrowLeft, FaArrowRight, FaSyncAlt, FaExternalLinkAlt, FaCreditCard } from 'react-icons/fa';
import ModalUploadCertificado from './ModalUploadCertificado';
import ModalNovaEmpresa from '../components/ModalNovaEmpresa';
import ModalGerenciarAssinatura from '../components/ModalGerenciarAssinatura';
import CardEmpresa from '../components/CardEmpresa';
import TrocarSenha from '../components/TrocarSenha';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';

const db = getFirestore();
// ✅ mesma região das suas Cloud Functions
const functions = getFunctions(undefined, 'southamerica-east1');

// ✅ fallback melhor para produção
const BACKEND_URL =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_BACKEND_URL) ||
  process.env.REACT_APP_BACKEND_URL ||
  'https://api.gestorproapp.com.br';

/* ------------------------- Helpers ------------------------- */
async function authHeaderJSON() {
  const user = auth.currentUser;
  if (!user) throw new Error('Usuário não autenticado.');
  const token = await user.getIdToken(true);
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

function validarCNPJ(cnpj) {
  cnpj = cnpj.replace(/\D+/g, '');
  if (cnpj.length !== 14 || /^([0-9])\1+$/.test(cnpj)) return false;
  let t = cnpj.length - 2;
  let d = cnpj.substring(t);
  let d1 = parseInt(d.charAt(0)), d2 = parseInt(d.charAt(1));
  let calc = x => {
    let n = cnpj.substring(0, x);
    let s = 0, pos = x - 7;
    for (let i = x; i >= 1; i--) {
      s += parseInt(n.charAt(x - i)) * pos--;
      if (pos < 2) pos = 9;
    }
    return (s % 11 < 2) ? 0 : 11 - s % 11;
  };
  return calc(t) === d1 && calc(t + 1) === d2;
}

const EMPTY_FORM = {
  razaoSocial: '',
  nomeFantasia: '',
  regimeTributario: '1',
  cnpj: '',
  inscricaoMunicipal: '',
  inscricaoEstadual: '',
  email: '',
  telefone: '',
  cep: '',
  endereco: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  estado: '',
  codigoIBGE: '',
  cnae: '',
  codigoTributarioMunicipio: '',
  itemListaServico: '',
  loginPrefeitura: '',
  senhaPrefeitura: '',
  obs: ''
};

async function syncClaims() {
  try {
    const user = auth.currentUser;
    if (!user) return;
    const call = httpsCallable(functions, 'atualizarMinhasClaims');
    await call();
    await user.getIdToken(true);
    const t = await user.getIdTokenResult();
    console.log('Claims atualizadas:', t.claims);
  } catch (e) {
    console.warn('Falha ao sincronizar claims', e);
  }
}

/** ================= Assinatura helpers/UI ================== */
function currencyBRLFromCents(cents) {
  if (cents == null) return '—';
  try {
    return (Number(cents) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  } catch {
    return '—';
  }
}
function fmtDate(d) {
  if (!d) return '—';
  try {
    const dt = typeof d === 'string' ? new Date(d) : d.toDate?.() || new Date(d);
    return dt.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
  } catch {
    return '—';
  }
}
const PLAN_LABEL = { box_start: 'Box Start', box_turbo: 'Box Turbo', box_master: 'Box Master' };
const PERIOD_LABEL = { mensal: 'Mensal', semestral: 'Semestral', anual: 'Anual' };

async function fetchMinhaAssinatura(empresaId) {
  const headers = await authHeaderJSON();
  const url = `${BACKEND_URL.replace(/\/+$/, '')}/billing/minha-assinatura?empresaId=${encodeURIComponent(empresaId)}`;
  const res = await fetch(url, { method: 'GET', headers });
  const txt = await res.text();
  let data = {};
  try { data = JSON.parse(txt); } catch { data = {}; }
  if (!res.ok) {
    const msg = data?.mensagem || data?.message || 'Erro ao buscar assinatura';
    throw new Error(msg);
  }
  return data;
}

/* ============================ Componente ============================ */
const Empresas = () => {
  const [empresas, setEmpresas] = useState([]);
  const [busca, setBusca] = useState('');
  const [formVisivel, setFormVisivel] = useState(false);
  const [pagina, setPagina] = useState(1);
  const [ultimoDoc, setUltimoDoc] = useState(null);
  const [temMaisPaginas, setTemMaisPaginas] = useState(false);
  const [carregandoCep, setCarregandoCep] = useState(false);
  const [erros, setErros] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [empresaSelecionada, setEmpresaSelecionada] = useState(null);
  const [editandoId, setEditandoId] = useState(null);
  const [userId, setUserId] = useState(null); // 🔹 estado reativo do usuário
  const [carregandoEmpresas, setCarregandoEmpresas] = useState(true); // 🔹 gating de carregamento

  // 🔹 Assinatura (vinculada à 1ª empresa do usuário)
  const [assinLoading, setAssinLoading] = useState(false);
  const [assinError, setAssinError] = useState(null);
  const [assin, setAssin] = useState(null);

  // 🔹 Modal de gerenciamento (cartão/plano)
  const [openGerenciar, setOpenGerenciar] = useState(false);

  // carrega rascunho do localStorage
  const [form, setForm] = useState(() => {
    try {
      const draft = JSON.parse(localStorage.getItem('novaEmpresaDraft') || 'null');
      return draft ? { ...EMPTY_FORM, ...draft } : { ...EMPTY_FORM };
    } catch {
      return { ...EMPTY_FORM };
    }
  });

  // 🔹 garante userId em prod/dev
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUserId(u?.uid || null));
    return () => unsub();
  }, []);

  // 🔹 se não houver usuário, encerra o "carregando" para não abrir modal automaticamente
  useEffect(() => {
    if (!userId) setCarregandoEmpresas(false);
  }, [userId]);

  // Buscar empresas do usuário logado
  useEffect(() => {
    if (userId) buscarEmpresas(false, true, userId);
  }, [userId]);

  // Persiste rascunho no localStorage a cada mudança (sem senha)
  useEffect(() => {
    const { senhaPrefeitura, ...safe } = form;
    try { localStorage.setItem('novaEmpresaDraft', JSON.stringify(safe)); } catch {}
  }, [form]);

  // Abre modal automaticamente se houver rascunho e nenhuma empresa (após carregar)
  useEffect(() => {
    if (carregandoEmpresas) return; // ⛔ não abra durante o fetch
    if (!formVisivel && empresas.length === 0) {
      try {
        const draft = JSON.parse(localStorage.getItem('novaEmpresaDraft') || 'null');
        if (draft && (draft.razaoSocial || draft.cnpj || draft.email)) {
          setForm({ ...EMPTY_FORM, ...draft });
          setFormVisivel(true);
        }
      } catch {}
    }
  }, [carregandoEmpresas, empresas.length, formVisivel]);

  // 🔹 Busca a assinatura associada à primeira (e única) empresa
  useEffect(() => {
    const primeiraId = empresas?.[0]?.id || null;
    if (!primeiraId) {
      setAssin(null);
      setAssinError(null);
      return;
    }
    let abort = false;
    (async () => {
      try {
        setAssinLoading(true);
        setAssinError(null);
        const data = await fetchMinhaAssinatura(primeiraId);
        if (abort) return;
        setAssin(data || null);
      } catch (e) {
        if (abort) return;
        setAssin(null);
        setAssinError(e?.message || 'Não foi possível carregar a assinatura.');
      } finally {
        if (!abort) setAssinLoading(false);
      }
    })();
    return () => { abort = true; };
  }, [empresas]);

  const validarFormulario = () => {
    const novosErros = {};
    if (!form.razaoSocial.trim()) novosErros.razaoSocial = 'Razão Social é obrigatória';
    if (!form.nomeFantasia.trim()) novosErros.nomeFantasia = 'Nome Fantasia é obrigatório';
    if (!form.regimeTributario) novosErros.regimeTributario = 'Regime Tributário é obrigatório';
    if (!form.cnpj.trim()) {
      novosErros.cnpj = 'CNPJ é obrigatório';
    } else {
      const rawCnpj = form.cnpj.replace(/\D/g, '');
      if (!validarCNPJ(rawCnpj)) novosErros.cnpj = 'CNPJ inválido';
    }
    if (!form.inscricaoMunicipal.trim()) novosErros.inscricaoMunicipal = 'Inscrição Municipal é obrigatória';
    if (!form.email.trim()) novosErros.email = 'E-mail é obrigatório';
    if (!form.telefone.trim()) novosErros.telefone = 'Telefone é obrigatório';
    if (!form.cep.trim()) novosErros.cep = 'CEP é obrigatório';
    if (!form.endereco.trim()) novosErros.endereco = 'Endereço é obrigatório';
    if (!form.numero.trim()) novosErros.numero = 'Número é obrigatório';
    if (!form.bairro.trim()) novosErros.bairro = 'Bairro é obrigatório';
    if (!form.cidade.trim()) novosErros.cidade = 'Cidade é obrigatória';
    if (!form.estado.trim()) novosErros.estado = 'Estado é obrigatório';
    setErros(novosErros);
    return Object.keys(novosErros).length === 0;
  };

  const buscarEmpresas = async (continuar = false, resetar = false, uid = null) => {
    const currentUserId = uid || auth.currentUser?.uid;

    if (!currentUserId) {
      setEmpresas([]);
      setCarregandoEmpresas(false);
      return;
    }

    setCarregandoEmpresas(true);
    try {
      let q = query(
        collection(db, 'empresas'),
        where('userId', '==', currentUserId),
        orderBy('razaoSocial'),
        limit(12)
      );
      if (continuar && ultimoDoc && !resetar) {
        q = query(
          collection(db, 'empresas'),
          where('userId', '==', currentUserId),
          orderBy('razaoSocial'),
          startAfter(ultimoDoc),
          limit(12)
        );
      }
      const snapshot = await getDocs(q);
      const docs = snapshot.docs;
      setEmpresas(docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setUltimoDoc(docs[docs.length - 1]);
      setTemMaisPaginas(docs.length === 12);
    } catch (err) {
      console.error('Erro ao buscar empresas:', err);
      setEmpresas([]);
      setUltimoDoc(null);
      setTemMaisPaginas(false);
    } finally {
      setCarregandoEmpresas(false);
    }
  };

  const handleSalvar = async () => {
    if (!validarFormulario() || !auth.currentUser?.uid) return;

    const dadosParaSalvar = {
      ...form,
      inscricaoEstadual:
        (form.inscricaoEstadual?.toUpperCase() === 'ISENTO' || !form.inscricaoEstadual)
          ? 'ISENTO'
          : form.inscricaoEstadual.replace(/\D/g, ''),
    };

    try {
      if (editandoId) {
        const { criadoEm, certificadoStatus, userId: _, ...dadosEditaveis } = dadosParaSalvar;

        await updateDoc(doc(db, 'empresas', editandoId), {
          ...dadosEditaveis,
          userId: auth.currentUser.uid
        });

        const res = await fetch(
          `${BACKEND_URL}/api/focus/atualizar-empresa/${editandoId}`,
          {
            method: 'PUT',
            headers: await authHeaderJSON(),
            body: JSON.stringify({
              ...dadosEditaveis,
              userId: auth.currentUser.uid,
              login_responsavel: dadosEditaveis.loginPrefeitura || undefined,
              senha_responsavel: dadosEditaveis.senhaPrefeitura || undefined,
            })
          }
        );

        if (!res.ok) {
          const erro = await res.json().catch(() => ({}));
          throw new Error(erro.mensagem || 'Erro ao atualizar empresa na Focus');
        }

        await syncClaims();

        Swal.fire({
          icon: 'success',
          title: 'Sucesso',
          text: 'Empresa atualizada com sucesso!',
          timer: 2000,
          showConfirmButton: false
        });
      } else {
        await addDoc(collection(db, 'empresas'), {
          ...dadosParaSalvar,
          userId: auth.currentUser.uid,
          certificadoStatus: 'pendente',
          criadoEm: Timestamp.now()
        });

        await syncClaims();

        Swal.fire({
          icon: 'success',
          title: 'Sucesso',
          text: 'Empresa salva com sucesso!',
          timer: 2000,
          showConfirmButton: false
        });
      }

      setFormVisivel(false);
      setEditandoId(null);
      setForm({ ...EMPTY_FORM });
      try { localStorage.removeItem('novaEmpresaDraft'); } catch {}

      setPagina(1);
      buscarEmpresas(false, true);
    } catch (error) {
      console.error('Erro ao salvar empresa:', error);
      Swal.fire('Erro', error.message || 'Ocorreu um erro ao salvar a empresa.', 'error');
    }
  };

  const handleExcluir = async (id) => {
    const result = await Swal.fire({
      title: 'Deseja excluir esta empresa?',
      text: 'Esta ação não poderá ser desfeita.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        const res = await fetch(`${BACKEND_URL}/api/focus/excluir-empresa/${id}`, {
          method: 'DELETE',
          headers: await authHeaderJSON()
        });
        if (!res.ok) {
          const erro = await res.json().catch(() => ({}));
          throw new Error(erro.mensagem || 'Erro ao excluir empresa na Focus');
        }

        await deleteDoc(doc(db, 'empresas', id));
        setPagina(1);
        await buscarEmpresas(false, true);

        await syncClaims();

        Swal.fire('Excluída!', 'A empresa foi removida.', 'success');
      } catch (error) {
        Swal.fire('Erro', error.message || 'Ocorreu um erro ao excluir a empresa.', 'error');
      }
    }
  };

  const atualizarStatusCertificado = async (empresaId, status = 'ok') => {
    await updateDoc(doc(db, 'empresas', empresaId), { certificadoStatus: status });
    buscarEmpresas(false, true);
  };

  const handleEditar = (empresa) => {
    setEditandoId(empresa.id);
    const { id, criadoEm, certificadoStatus, ...rest } = empresa;
    setForm({ ...EMPTY_FORM, ...rest });
    setFormVisivel(true);
  };

  const empresasFiltradas = empresas.filter((e) =>
    e.razaoSocial?.toLowerCase().includes(busca.toLowerCase()) ||
    e.cnpj?.replace(/\D/g, '').includes(busca.replace(/\D/g, '')) ||
    e.inscricaoMunicipal?.includes(busca)
  );

  // ===================== UI ======================
  return (
    <div className="flex h-full relative">
      <main className="flex-grow max-w-screen-xl w-full mx-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-3xl font-bold text-gray-800 dark:text-gray-100">Empresa cadastrada</h3>
          <div className="flex items-center gap-4">
            <input
              type="text"
              placeholder="Buscar"
              className="border px-3 py-2 rounded-md w-64 bg-white dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />

            <button
              onClick={() => {
                setEditandoId(null);
                try {
                  const draft = JSON.parse(localStorage.getItem('novaEmpresaDraft') || 'null');
                  setForm(draft ? { ...EMPTY_FORM, ...draft } : { ...EMPTY_FORM });
                } catch {
                  setForm({ ...EMPTY_FORM });
                }
                setFormVisivel(true);
              }}
              className={`inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3.5 font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition
                ${!userId || empresas.length > 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={!userId || empresas.length > 0}
            >
              <FaPlus /> Nova Empresa
            </button>
          </div>
        </div>

        {/* ===== Painel da Assinatura ===== */}
        {empresas?.[0]?.id && (
          <section className="mb-6">
            <div className="rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-gray-900 p-5 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-200">
                    <FaCreditCard />
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-gray-900 dark:text-white">Sua assinatura</h4>
                    <p className="text-sm text-gray-600 dark:text-slate-300">
                      Empresa: <b>{empresas[0].nomeFantasia || empresas[0].razaoSocial || empresas[0].id}</b>
                    </p>
                  </div>
                </div>

                <button
                  onClick={async () => {
                    const id = empresas[0].id;
                    try {
                      setAssinLoading(true);
                      setAssinError(null);
                      const data = await fetchMinhaAssinatura(id);
                      setAssin(data || null);
                    } catch (e) {
                      setAssinError(e?.message || 'Falha ao recarregar');
                    } finally {
                      setAssinLoading(false);
                    }
                  }}
                  className="inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-slate-600 px-3 py-2 text-sm text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800"
                >
                  <FaSyncAlt className={assinLoading ? 'animate-spin' : ''} /> Atualizar
                </button>
              </div>

              {/* estados */}
              {assinLoading && (
                <div className="mt-4 text-sm text-gray-600 dark:text-slate-300">Carregando assinatura…</div>
              )}
              {assinError && !assinLoading && (
                <div className="mt-4 rounded-lg border border-rose-300 bg-rose-50 text-rose-700 p-3">
                  {assinError}
                </div>
              )}

              {!assinLoading && !assinError && (
                <>
                  {/* Sem assinatura */}
                  {!assin && (
                    <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div className="text-gray-700 dark:text-slate-300">
                        Nenhuma assinatura ativa para esta empresa.
                      </div>
                      <a
                        href={`/planos?empresaId=${encodeURIComponent(empresas[0].id)}`}
                        className="inline-flex items-center justify-center rounded-xl bg-[#004072] px-4 py-2.5 text-white font-semibold hover:brightness-110"
                      >
                        Escolher plano
                      </a>
                    </div>
                  )}

                  {/* Com assinatura */}
                  {assin && (
                    <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                      <div className="rounded-xl bg-slate-50 dark:bg-slate-800/60 p-4">
                        <div className="text-xs text-slate-500">Plano</div>
                        <div className="text-base font-semibold text-slate-900 dark:text-white">
                          {PLAN_LABEL[assin.plan_key] || assin.plan_key || '—'}
                        </div>
                      </div>

                      <div className="rounded-xl bg-slate-50 dark:bg-slate-800/60 p-4">
                        <div className="text-xs text-slate-500">Ciclo</div>
                        <div className="text-base font-semibold text-slate-900 dark:text-white">
                          {PERIOD_LABEL[assin.billing_cycle] || assin.billing_cycle || '—'}
                        </div>
                      </div>

                      <div className="rounded-xl bg-slate-50 dark:bg-slate-800/60 p-4">
                        <div className="text-xs text-slate-500">Status</div>
                        <div className="text-base font-semibold text-slate-900 dark:text-white capitalize">
                          {assin.status || '—'}
                        </div>
                      </div>

                      <div className="rounded-xl bg-slate-50 dark:bg-slate-800/60 p-4">
                        <div className="text-xs text-slate-500">Próxima cobrança</div>
                        <div className="text-base font-semibold text-slate-900 dark:text-white">
                          {fmtDate(assin.next_charge_date)} · {currencyBRLFromCents(assin.next_charge_cents)}
                        </div>
                      </div>

                      {/* Linha 2 */}
                      <div className="rounded-xl bg-slate-50 dark:bg-slate-800/60 p-4">
                        <div className="text-xs text-slate-500">Teste grátis até</div>
                        <div className="text-base font-semibold text-slate-900 dark:text-white">
                          {fmtDate(assin.trial_end_date)}
                        </div>
                      </div>

                      <div className="rounded-xl bg-slate-50 dark:bg-slate-800/60 p-4">
                        <div className="text-xs text-slate-500">Período atual (fim)</div>
                        <div className="text-base font-semibold text-slate-900 dark:text-white">
                          {fmtDate(assin.current_period_end)}
                        </div>
                      </div>

                      <div className="rounded-xl bg-slate-50 dark:bg-slate-800/60 p-4">
                        <div className="text-xs text-slate-500">Notas incluídas</div>
                        <div className="text-base font-semibold text-slate-900 dark:text-white">
                          {typeof assin.invoices_included === 'number' ? assin.invoices_included : '—'}
                        </div>
                      </div>

                      <div className="rounded-xl bg-slate-50 dark:bg-slate-800/60 p-4">
                        <div className="text-xs text-slate-500">Consumo do mês</div>
                        <div className="text-base font-semibold text-slate-900 dark:text-white">
                          {typeof assin.invoices_used === 'number' ? assin.invoices_used : '—'}
                        </div>
                      </div>

                      <div className="sm:col-span-2 lg:col-span-4 mt-2 flex flex-wrap items-center gap-3">
                        <a
                          href={
                            assin.billing_portal_url
                              ? assin.billing_portal_url
                              : `/planos?empresaId=${encodeURIComponent(empresas[0].id)}`
                          }
                          target={assin.billing_portal_url ? '_blank' : undefined}
                          rel={assin.billing_portal_url ? 'noreferrer' : undefined}
                          className="inline-flex items-center gap-2 rounded-xl border border-gray-300 dark:border-slate-600 px-4 py-2 font-semibold text-gray-800 dark:text-white hover:bg-gray-100 dark:hover:bg-slate-800"
                        >
                          Gerenciar {assin.billing_portal_url && <FaExternalLinkAlt className="opacity-70" />}
                        </a>

                        <button
                          type="button"
                          onClick={() => setOpenGerenciar(true)}
                          className="inline-flex items-center gap-2 rounded-xl bg-[#004072] px-4 py-2 text-white font-semibold hover:brightness-110"
                        >
                          Alterar forma de pagamento
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </section>
        )}

        {/* ===== Segurança / Trocar senha ===== */}
        {userId && (
          <section className="mb-6">
            <div className="rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-gray-900 p-5 shadow-sm">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h4 className="text-lg font-bold text-gray-900 dark:text-white">Segurança</h4>
                  <p className="text-sm text-gray-600 dark:text-slate-300">
                    Gerencie sua senha de acesso.
                  </p>
                </div>
              </div>

              <div className="mt-4">
                <TrocarSenha embedded />
              </div>
            </div>
          </section>
        )}

        {/* ===== Lista de empresas ===== */}
        {!userId && (
          <div className="mb-6 p-4 bg-yellow-100 text-yellow-800 border border-yellow-200 rounded dark:bg-yellow-200 dark:text-yellow-900">
            Nenhum <b>usuário</b> encontrado. Faça login novamente para acessar suas empresas.
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {empresasFiltradas.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 col-span-full">Nenhuma empresa encontrada.</p>
          ) : (
            empresasFiltradas.map(e => (
              <CardEmpresa
                key={e.id}
                empresa={e}
                onEditar={() => handleEditar(e)}
                onExcluir={() => handleExcluir(e.id)}
                onEnviarCertificado={() => {
                  setEmpresaSelecionada(e.id);
                  setModalOpen(true);
                }}
              />
            ))
          )}
        </div>

        <div className="flex justify-center items-center gap-4 mt-6">
          <button
            onClick={() => {
              setPagina(p => Math.max(p - 1, 1));
              buscarEmpresas(false, true);
            }}
            disabled={pagina <= 1}
            className="flex items-center gap-2 px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-sm rounded disabled:opacity-50"
          >
            <FaArrowLeft /> Anterior
          </button>
          <span className="text-sm text-gray-700 dark:text-gray-200">Página {pagina}</span>
          <button
            onClick={() => {
              buscarEmpresas(true);
              setPagina(p => p + 1);
            }}
            disabled={!temMaisPaginas}
            className="flex items-center gap-2 px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-sm rounded disabled:opacity-50"
          >
            Próxima <FaArrowRight />
          </button>
        </div>
      </main>

      {/* 🔒 Monte os modais somente quando estiverem abertos */}
      {formVisivel && (
        <ModalNovaEmpresa
          isOpen={formVisivel}
          onClose={() => { setFormVisivel(false); setEditandoId(null); }}
          onSave={handleSalvar}
          form={form}
          setForm={setForm}
          erros={erros}
          editandoId={editandoId}
          carregandoCep={carregandoCep}
        />
      )}

      {modalOpen && (
        <ModalUploadCertificado
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          empresaId={empresaSelecionada}
          onUploadSuccess={() => {
            setModalOpen(false);
            atualizarStatusCertificado(empresaSelecionada, 'ok');
          }}
        />
      )}

      {openGerenciar && empresas?.[0]?.id && (
        <ModalGerenciarAssinatura
          open={openGerenciar}
          onClose={() => setOpenGerenciar(false)}
          empresaId={empresas[0].id}
          currentPlanKey={assin?.plan_key}
          currentCycle={assin?.billing_cycle}
        />
      )}
    </div>
  );
};

export default Empresas;
